import {GridItem, Hr, Link} from '@constellation/core';
import {memo} from 'react';
import {type otherMonthlyProps} from '../../../../../types/cardPaymentServiceDetails.js';
import breakPoint from '../../../../../utils/breakPoint.js';
import {OTHER_MONTHLY_CHARGES} from '../../../../../utils/constants/public/psr.js';
import {getPsrLabel} from '../../../../../utils/index.js';
import {
  getQuotationPrice,
  getTotalPercent,
} from '../../../../../utils/quotation.js';
import {
  GridItemHeadingLine,
  MonthlyGridEven,
  MonthlyGridPOS,
  MonthlyGridServiceFee,
  OtherSectionDiv,
  OtherSectionHeading,
  OtherSectionText,
  OtherSectionValues,
} from '../Styles/Styles.js';

const OtherMonthlyCharges = memo((props: otherMonthlyProps) => {
  const {monthlyCharges, mmsc} = props;
  const gateway = monthlyCharges?.gateway;
  const transactionValue = gateway?.transaction?.value
    ? getTotalPercent(gateway?.transaction?.value)
    : '0p';
  const pos = monthlyCharges?.pos;
  const pciDss = monthlyCharges?.pciDss;

  return (
    <div aria-describedby={OTHER_MONTHLY_CHARGES} role='table'>
      <OtherSectionDiv bgColor='neutral2'>
        <OtherSectionHeading marginBottom='02' marginLeft='03' size='s3'>
          <div id={OTHER_MONTHLY_CHARGES}>
            {getPsrLabel(
              'cardPaymentServicesDetails.otherSection.monthlyCharges.labelText'
            )}
          </div>
        </OtherSectionHeading>

        <div role='rowgroup'>
          <GridItemHeadingLine
            aria-hidden={true}
            marginBottom='none'
            marginTop='none'
          />
          <div role='row'>
            <MonthlyGridPOS>
              <GridItem xs={7}>
                <OtherSectionText>
                  <div role='rowheader'>
                    {getPsrLabel(
                      'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label1'
                    )}
                  </div>
                </OtherSectionText>
              </GridItem>

              <OtherSectionValues xs={5}>
                <OtherSectionText>
                  <div role='cell'>
                    {pos?.description ||
                      getPsrLabel('cardPaymentServicesDetails.notApplicable')}
                  </div>
                </OtherSectionText>
              </OtherSectionValues>
            </MonthlyGridPOS>
          </div>
          <Hr aria-hidden={true} marginBottom='none' marginTop='none' />
          <div role='row'>
            <MonthlyGridEven>
              <GridItem xs={7}>
                <OtherSectionText>
                  <div role='rowheader'>
                    {getPsrLabel(
                      'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label2'
                    )}
                    <small>
                      <sup>
                        <Link
                          aria-label={`footnote ${getPsrLabel(
                            'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label2Ref'
                          )}`}
                          href='#capturingpaymentdata'
                          id={`capturingpaymentdata-ref${breakPoint}`}
                        >
                          {getPsrLabel(
                            'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label2Ref'
                          )}
                        </Link>
                      </sup>
                    </small>
                  </div>
                </OtherSectionText>
              </GridItem>
              <OtherSectionValues xs={5}>
                <OtherSectionText>
                  <div role='cell'>
                    {gateway?.applicable
                      ? `${getQuotationPrice(
                          gateway?.monthly?.value,
                          true
                        )} monthly + ${transactionValue} per transaction`
                      : getPsrLabel('cardPaymentServicesDetails.notApplicable')}
                  </div>
                </OtherSectionText>
              </OtherSectionValues>
            </MonthlyGridEven>
          </div>
          <Hr aria-hidden={true} marginBottom='none' marginTop='none' />
          <div role='row'>
            <MonthlyGridServiceFee>
              <GridItem xs={6}>
                <OtherSectionText>
                  <div role='rowheader'>
                    {getPsrLabel(
                      'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label3'
                    )}
                    <small>
                      <sup>
                        <Link
                          aria-label={`footnote ${getPsrLabel(
                            'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label3Ref'
                          )}`}
                          href='#pcidssfee'
                          id={`pcidssfee-ref${breakPoint}`}
                        >
                          {getPsrLabel(
                            'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label3Ref'
                          )}
                        </Link>
                      </sup>
                    </small>
                  </div>
                </OtherSectionText>
              </GridItem>
              <OtherSectionValues xs={6}>
                <OtherSectionText>
                  <div role='cell'>{`£${pciDss?.amount?.value || 0}`}</div>
                </OtherSectionText>
              </OtherSectionValues>
            </MonthlyGridServiceFee>
          </div>
          <Hr aria-hidden={true} marginBottom='none' marginTop='none' />
          <div role='row'>
            <MonthlyGridEven>
              <GridItem xs={8}>
                <OtherSectionText>
                  <div role='rowheader'>
                    {getPsrLabel(
                      'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label4'
                    )}
                    <small>
                      <sup>
                        <Link
                          aria-label={`footnote ${getPsrLabel(
                            'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label4Ref'
                          )}`}
                          href='#notmeetminagreementamount'
                          id={`notmeetminagreementamount-ref${breakPoint}`}
                        >
                          {getPsrLabel(
                            'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label4Ref'
                          )}
                        </Link>
                      </sup>
                    </small>
                  </div>
                </OtherSectionText>
              </GridItem>
              <OtherSectionValues xs={4}>
                <OtherSectionText>
                  <div role='cell'>{getQuotationPrice(mmsc?.value, true)}</div>
                </OtherSectionText>
              </OtherSectionValues>
            </MonthlyGridEven>
          </div>
          <Hr aria-hidden={true} marginBottom='none' marginTop='none' />
        </div>
      </OtherSectionDiv>
    </div>
  );
});

export default OtherMonthlyCharges;
